import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { billingService, queryKeys } from '@/services';
import { useDebouncedValue } from '@/hooks/common';

/** Pairs per request. One row per pair, so this is also the rows-per-scroll. */
const PAGE_SIZE = 100;

/**
 * The counterparty's book as instrument + trade-type options for the manual
 * adjustment form (LR-563).
 *
 * This is what turns the product's "выбираем какую сделку зааджастить" into a
 * pick the storage model can honour: the operator chooses off the real book
 * rather than retyping a symbol and recalling the trade type, while the booking
 * stays keyed by instrument + trade type.
 *
 * The collapse to pairs, the search and the paging all happen on the SERVER, in
 * one grouped query per page. Two earlier shapes failed on a real book and are
 * worth not repeating:
 *
 * - Reading a slab of the blotter list and grouping it in the browser. That
 *   endpoint serializes ~100 positions at 103 fields each and sweeps TickDB for
 *   COB prices per page — seconds of work for data a dropdown never shows.
 * - Paging over TRADES while rendering PAIRS. A page of 100 trades on three
 *   instruments collapsed to three rows, so the list never overflowed, the
 *   scroll event never fired and the book silently ended at page one.
 *
 * Paging over PAIRS has neither problem: one fetched row is one rendered row, so
 * the list grows in step with what was loaded and scrolling advances it.
 */
export const useCounterpartyTradeOptions = (
  counterpartyId: number | null,
  query: string,
  /** Only query while the picker is open — a сторно never opens it at all. */
  active: boolean
) => {
  const trimmed = query.trim();
  const debouncedQuery = useDebouncedValue(trimmed, 250);
  const enabled =
    active && counterpartyId !== null && Number.isFinite(counterpartyId);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.billing.adjustmentTradeOptions({
        counterparty: String(counterpartyId),
        search: debouncedQuery,
      }),
      initialPageParam: 0,
      queryFn: ({ pageParam }) =>
        billingService.getAdjustmentTradeOptions({
          counterparty: counterpartyId as number,
          ...(debouncedQuery ? { search: debouncedQuery } : {}),
          limit: PAGE_SIZE,
          offset: pageParam as number,
        }),
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((sum, p) => sum + p.results.length, 0);
        return loaded < lastPage.count ? loaded : undefined;
      },
      enabled,
      // The book does not move while a correction is being typed, and a refetch
      // could reorder the options between the operator's mousedown and click.
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    });

  const tradeOptions = useMemo(
    () => (data?.pages ?? []).flatMap((p) => p.results),
    [data]
  );

  return {
    tradeOptions,
    isTradesLoading: enabled && isLoading,
    /** The typed query has not reached the server yet (debounce in flight), so
     *  what is on screen answers an older query. */
    isSearchPending: enabled && trimmed !== debouncedQuery,
    loadMoreTrades: fetchNextPage,
    hasMoreTrades: Boolean(hasNextPage),
    isLoadingMoreTrades: isFetchingNextPage,
    loadedTradeCount: tradeOptions.length,
    /** undefined while the first page is in flight — 0 would read as "none". */
    totalTradeCount: data?.pages[0]?.count,
  };
};
